import { FC, useCallback, useEffect, useState } from 'react';

import { useGenericSelectionPanelFunctions } from '@/features/selectionsPanel/hooks/useGenericSelectionPanelFunctions';
import { useGetLocationInfo } from '@/features/selectionsPanel/hooks/useGetLocationInfo';
import type { IUploadFeature } from '@/models/features';

import { Card } from '../card';
import { CardTitle } from '../card/cardTitle';
import { LineItem } from './lineItem';

type Props = {
  feature: IUploadFeature;
  idx: number;
};

export const UploadCard: FC<Props> = ({ feature, idx }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [cardHasBeenViewed, setCardHasBeenViewed] = useState(false);
  const { text: locationText, isLoading } = useGetLocationInfo({
    feature,
    shouldFetch: cardHasBeenViewed,
  });
  const {
    goToGeometryLocation,
    handleMouseOff,
    handleMouseOn,
    handleUploadRemove,
  } = useGenericSelectionPanelFunctions();

  const handleOnClick = useCallback(
    (e) => {
      e.stopPropagation();
      goToGeometryLocation(feature);
    },
    [feature, goToGeometryLocation]
  );

  const handleOnClose = useCallback(
    (e) => {
      e.stopPropagation();
      handleUploadRemove(feature);
    },
    [feature, handleUploadRemove]
  );

  useEffect(() => {
    if (isHovered) {
      handleMouseOn(feature);
    } else {
      handleMouseOff(feature);
    }
  }, [feature, handleMouseOff, handleMouseOn, isHovered]);

  return (
    <Card
      onCardInView={() => {
        if (!cardHasBeenViewed) setCardHasBeenViewed(true);
      }}
      onClick={handleOnClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardTitle feature={feature} idx={idx} onClose={handleOnClose} />
      <LineItem isLoading={isLoading} label="Location" value={locationText} />
    </Card>
  );
};
